import { Suspense, useCallback, useRef, useState } from 'react';
import { Button, Header, Island, OverlayContent, Text, VStack } from '@direct-frontend/components';
import { noop } from '@direct-frontend/stdlib';
import { useGetIntegrationSettingsStocks } from '#entities/integration';
import { PageStub } from '#shared/components/PageStub';
import { useDeferModalUnmount } from '#shared/lib/hooks';
import { getWarehouseHeaderCaption, type SupportedIntegrationTypes } from '#shared/lib/integrations';
import { InfoBlock } from '#shared/ui/InfoBlock';
import { Modal } from '#shared/ui/Modal';
import { CompareWarehousesMiniGrid } from './components/CompareWarehousesMiniGrid';
import { useHandleSave } from './lib/useHandleSave';
import { closeCompareWarehousesOzonFbsModal, useCompareWarehousesOzonFbsModal } from './model/modal';
import { type WarehouseToCompare } from './types';

const DATA_TEST_ID = 'CompareWarehousesOzonFbsModal';

export const CompareWarehousesOzonFbsModal = () => {
    const { isOpen } = useCompareWarehousesOzonFbsModal();
    const { shouldRenderContent, onModalAnimationEnd } = useDeferModalUnmount(isOpen);

    return (
        <Modal
            isOpen={isOpen}
            size="overlay-small"
            onClose={closeCompareWarehousesOzonFbsModal}
            onAnimationEnd={onModalAnimationEnd}
        >
            {shouldRenderContent && (
                <Suspense
                    fallback={
                        <CompareWarehousesOzonFbsModalContentStub
                            isLoading={false}
                            handleSave={noop}
                            onClose={closeCompareWarehousesOzonFbsModal}
                            dataTestId={DATA_TEST_ID}
                        />
                    }
                >
                    <CompareWarehousesOzonFbsModalContent onClose={closeCompareWarehousesOzonFbsModal} />
                </Suspense>
            )}
        </Modal>
    );
};

const CompareWarehousesOzonFbsModalContent = ({ onClose }: { onClose: VoidFunction }) => {
    const { data: integrationSettings } = useGetIntegrationSettingsStocks();
    const integrationType = integrationSettings?.system_type as SupportedIntegrationTypes;
    const headerCaption = getWarehouseHeaderCaption(integrationType);

    const errorBlockRef = useRef<HTMLDivElement>(null);
    const [isLoading, toggleLoading] = useState(false);

    const [warehousesToCompare, setWarehousesToCompare] = useState<WarehouseToCompare[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const resetErrorMessage = useCallback(() => {
        setErrorMessage(undefined);
    }, []);

    const handleSetErrorMessage = useCallback((message: string) => {
        setErrorMessage(message);

        setTimeout(() => {
            errorBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 0);
    }, []);

    const handleSave = useHandleSave({
        warehousesToCompare,
        isLoading,
        toggleLoading,
        onClose,
        handleSetErrorMessage,
    });

    return (
        <OverlayContent
            bottomButtons={
                <BottomButtons
                    isLoading={isLoading}
                    handleSave={handleSave}
                    onClose={onClose}
                    dataTestId={DATA_TEST_ID}
                />
            }
        >
            <Island gap="28">
                <VStack>
                    <Header caption={headerCaption} level="h2">
                        Сопоставление складов
                    </Header>
                </VStack>
                <VStack gap="6">
                    <Text role="main">Укажите связанный склад</Text>
                    <CompareWarehousesMiniGrid
                        warehousesToCompare={warehousesToCompare}
                        setWarehousesToCompare={setWarehousesToCompare}
                        resetErrorMessage={resetErrorMessage}
                        integrationType={integrationType}
                    />
                    {errorMessage ? (
                        <div ref={errorBlockRef}>
                            <InfoBlock color="error" type="icon" caption={errorMessage} />
                        </div>
                    ) : null}
                </VStack>
            </Island>
        </OverlayContent>
    );
};

const CompareWarehousesOzonFbsModalContentStub = ({
    isLoading,
    handleSave,
    onClose,
    dataTestId,
}: {
    isLoading: boolean;
    handleSave: VoidFunction;
    onClose: VoidFunction;
    dataTestId: string;
}) => (
    <OverlayContent
        bottomButtons={
            <BottomButtons isLoading={isLoading} handleSave={handleSave} onClose={onClose} dataTestId={dataTestId} />
        }
    >
        <Island gap="28">
            <VStack>
                <Header level="h2">Сопоставление складов</Header>
            </VStack>
            <VStack gap="6">
                <Text role="main">Укажите связанный склад</Text>
                <PageStub />
            </VStack>
        </Island>
    </OverlayContent>
);

const BottomButtons = ({
    isLoading,
    handleSave,
    onClose,
    dataTestId,
}: {
    isLoading: boolean;
    handleSave: VoidFunction;
    onClose: VoidFunction;
    dataTestId: string;
}) => (
    <>
        <Button
            color="accent"
            size="m"
            isLoading={isLoading}
            onClick={handleSave}
            data-testid={`${dataTestId}.SaveButton`}
        >
            Сохранить
        </Button>
        <Button color="normal" size="m" onClick={onClose} data-testid={`${dataTestId}.CancelButton`}>
            Отмена
        </Button>
    </>
);
